import { useEffect, useRef } from 'react'
import Header from "@/components/Header"

export default function Terms() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Reduzido o número de partículas
    const particles: Array<{x: number, y: number, size: number, speedX: number, speedY: number, opacity: number}> = []
    const particleCount = window.innerWidth < 768 ? 15 : 25 // Menos partículas no mobile

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 3 + 1,
        speedX: (Math.random() * 1.5 - 0.75) * (window.innerWidth < 768 ? 0.3 : 1), // 70% mais lento no mobile
        speedY: (Math.random() * 1.5 - 0.75) * (window.innerWidth < 768 ? 0.3 : 1), // 70% mais lento no mobile
        opacity: Math.random() * 0.3 + 0.1
      })
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      particles.forEach((particle, i) => {
        particle.x += particle.speedX
        particle.y += particle.speedY

        if (particle.x < 0) particle.x = canvas.width
        if (particle.x > canvas.width) particle.x = 0
        if (particle.y < 0) particle.y = canvas.height
        if (particle.y > canvas.height) particle.y = 0

        ctx.beginPath()
        ctx.fillStyle = `rgba(37, 99, 235, ${particle.opacity})`
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
        ctx.fill()

        // Conectar partículas próximas
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j]
          const distance = Math.sqrt(Math.pow(particle.x - p2.x, 2) + Math.pow(particle.y - p2.y, 2))
          
          if (distance < 100) {
            ctx.beginPath()
            ctx.strokeStyle = `rgba(37, 99, 235, ${0.1 * (1 - distance / 100)})`
            ctx.lineWidth = 0.5
            ctx.moveTo(particle.x, particle.y)
            ctx.lineTo(p2.x, p2.y)
            ctx.stroke()
          }
        }
      })

      requestAnimationFrame(animate)
    }

    animate()

    return () => window.removeEventListener('resize', resizeCanvas)
  }, [])

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Gradientes animados em camadas */}
      <div className="absolute inset-0">
        {/* Gradiente base */}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
        
        {/* Gradiente radial pulsante */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent sm:animate-pulse-slow animate-pulse-slower" />
        
        {/* Gradiente diagonal animado */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 sm:animate-gradient-slow animate-gradient-slower" />
        
        {/* Vinheta suave nas bordas */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_50%,_rgba(0,0,0,0.1)_100%)]" />
      </div>
      
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: 0.3 }}
      />

      <div className="relative">
        <Header showBackButton />

        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16 pt-16">
            <h1 className="text-4xl font-bold mb-4">Termos de Uso</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Leia atentamente os termos e condições de uso do Simples.ECG
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-8">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-semibold mb-2">1. Aceitação dos Termos</h2>
                <p className="text-muted-foreground">
                  Ao acessar e usar o Simples.ECG, você concorda com estes termos de uso e todas as leis e regulamentos aplicáveis. Se você não concordar com algum destes termos, está proibido de usar o serviço.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-2">2. Uso do Serviço</h2>
                <p className="text-muted-foreground">
                  O Simples.ECG é uma ferramenta gratuita de auxílio à interpretação de eletrocardiogramas. 
                  Não substitui o diagnóstico médico profissional e deve ser usado apenas como ferramenta complementar. 
                  O serviço é oferecido gratuitamente pela Simples Health como parte de seu compromisso com a comunidade médica.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-2">3. Responsabilidade Médica</h2>
                <p className="text-muted-foreground">
                  Os usuários reconhecem que a interpretação final e as decisões clínicas baseadas nas análises fornecidas pelo Simples.ECG 
                  são de inteira responsabilidade do profissional de saúde. As sugestões de análise são fornecidas apenas como auxílio e não 
                  devem substituir o julgamento clínico, formação médica ou consulta com especialistas.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-2">4. Propriedade Intelectual</h2>
                <p className="text-muted-foreground">
                  Todo o conteúdo, recursos e funcionalidades disponíveis no Simples.ECG são propriedade da Simples Health ou de seus 
                  licenciadores e estão protegidos por leis de direitos autorais, marcas registradas e outras leis de propriedade intelectual.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-2">5. Política de Privacidade</h2>
                <p className="text-muted-foreground">
                  Ao usar o Simples.ECG, você concorda com nossa Política de Privacidade, que descreve como coletamos, usamos e compartilhamos 
                  suas informações. Todas as imagens enviadas são processadas em conformidade com regulamentações de privacidade e segurança de dados de saúde.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-2">6. Limitações de Responsabilidade</h2>
                <p className="text-muted-foreground">
                  A Simples Health não será responsável por quaisquer danos diretos, indiretos, incidentais, consequenciais, especiais ou punitivos 
                  decorrentes do uso ou incapacidade de usar o Simples.ECG, incluindo, mas não se limitando a, erros de diagnóstico ou decisões 
                  clínicas baseadas nas análises fornecidas.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-2">7. Alterações nos Termos</h2>
                <p className="text-muted-foreground">
                  Reservamo-nos o direito de modificar estes termos a qualquer momento. As alterações entrarão em vigor imediatamente após a 
                  publicação dos termos atualizados. É sua responsabilidade revisar periodicamente estes termos.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-2">8. Elegibilidade</h2>
                <p className="text-muted-foreground">
                  O Simples.ECG destina-se exclusivamente a profissionais de saúde devidamente registrados em seus respectivos conselhos profissionais. 
                  Ao usar o serviço, você confirma que é um profissional de saúde qualificado e autorizado a praticar em sua jurisdição.
                </p>
              </div>
            </div>

            <div className="text-sm text-muted-foreground text-center pt-8 border-t">
              <p>Última atualização: Fevereiro, 2024</p>
              <p className="mt-2">
                Para dúvidas sobre estes termos, entre em contato conosco: contato@simples.health
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 